<template lang="pug">
  .study-list
    v-toolbar.white(card, prominent)
      v-toolbar-title.light-blue--text.subheading Libro de Entradas
      v-spacer
      v-btn(icon, @click.prevent='toggleSearch', v-if='!mustShowSearch')
        v-icon search
      v-text-field.search-input(
        v-if='mustShowSearch'
        v-model='searchQuery'
        ref='search'
        @blur='toggleSearch'
        @keydown.enter='getStudies'
        name='search'
        label='Buscar estudio…'
        single-line
        autofocus
      )
      v-btn(icon, @click.prevent='clearSearch', v-if='mustShowSearch')
        v-icon close

    v-data-table(
      v-if='!loading'
      hide-actions
      no-data-text='No se encontraron estudios'
      :headers='table.headers'
      :items='studies'
      :pagination.sync='table.pagination'
    )
      template(slot='items', scope='props')
        tr(:class='{ noOrder: noOrder(props.item), noResults: !hasResults(props.item) }')
          td.text-xs-right(@click='openStudy(props.item)') {{ props.item.protocol }}
          td.text-xs-left(@click='openStudy(props.item)') {{ props.item.patient.first_name }} {{ props.item.patient.last_name }}
          td.text-xs-left(@click='openStudy(props.item)') {{ props.item.patient.dni }}
          td.text-xs-left(@click='openStudy(props.item)', v-if='props.item.studies')
            v-chip.light-green.lighten-1.white--text(v-if='props.item.studies.anemia', small) EA
            v-chip.light-blue.white--text(v-if='props.item.studies.anticoagulation', small) EAC
            v-chip.teal.lighten-2.white--text(v-if='props.item.studies.hemostasis', small) CCH
            v-chip.deep-purple.lighten-3.white--text(v-if='props.item.studies.myelogram', small) MD
            v-chip.orange.lighten-2.white--text(v-if='props.item.studies.thrombophilia', small) PT
            v-chip.pink.lighten-2.white--text(v-if='props.item.studies.compatibility', small) PCM
          td.text-xs-left(@click='openStudy(props.item)') {{ props.item.patient.medicare }}
          td.text-xs-left(@click='openStudy(props.item)') {{ props.item.date }}
          td.text-xs-right
            v-menu(bottom left)
              v-btn(icon, slot='activator')
                v-icon more_vert
              v-list
                v-list-tile(v-if='props.item.results.length', @click='print(props.item._id)')
                  v-list-tile-title
                    v-icon.mr-4 print
                    | Imprimir
                v-list-tile(v-if='props.item.results.length' @click='showEmailPrompt(props.item)')
                  v-list-tile-title
                    v-icon.mr-4 email
                    | Enviar…
                v-list-tile(@click='openStudy(props.item)')
                  v-list-tile-title
                    v-icon.mr-4 mode_edit
                    | Editar
      template(v-if='totalPages > 1', slot='footer')
        td(colspan='7'): v-pagination(
          :length='totalPages'
          :total-visible='10'
          v-model='pagination.current'
          circle
        )
    .loading(v-else)
      v-progress-circular.pink--text(
        indeterminate
        size='64'
      )
    v-dialog(v-model='displayEmailPrompt', width='400')
      v-card
        v-card-title: span.title Enviar por correo
        v-card-text
          v-text-field(
            label='E-mail'
            type='email'
            hint='Dirección de correo del destinatario'
            @keyup.enter='sendStudy(study)'
            @input='validEmail = true'
            required
            v-model='study.patient.email'
            :rules='[validEmail]'
          )
          v-checkbox(
            color='primary'
            label='Recordar correo para este paciente'
            v-model='updatePatientEmail'
          )
        v-card-actions
          v-spacer
          v-btn.blue-grey--text(
            flat
            @click='displayEmailPrompt = false'
          ) Cancelar
          v-btn.primary--text(
            :loading='sendingEmail'
            flat
            @click='sendStudy(study)'
          ) Enviar
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import moment from 'moment';
import studyPrint from '@/mixins/study-print';
import studySend from '@/mixins/study-send';

export default {
  name: 'App',

  data() {
    return {
      displaySearch: false,
      searchQuery: '',
      pagination: {
        current: 1,
      },
      study: {
        patient: {},
        results: [],
        studies: {},
      },
      table: {
        isFiltered: false,
        pagination: {
          sortBy: 'protocol',
          descending: true,
          rowsPerPage: 30,
        },
        headers: [
          { text: 'Protocolo', align: 'right', value: 'protocol' },
          { text: 'Nombre', align: 'left', value: 'name' },
          { text: 'DNI', align: 'left', value: 'document' },
          { text: 'Estudio', align: 'left', value: 'study' },
          { text: 'O. Social', align: 'left', value: 'medicare' },
          { text: 'Fecha', align: 'left', value: 'date' },
          { text: '', align: 'right', value: 'action', sortable: false },
        ],
      },
    };
  },

  created() {
    this.getStudies();
  },

  computed: {
    ...mapGetters({
      list: 'study/list',
    }),

    ...mapState('study', {
      loading: 'isFindPending',
    }),

    totalPages() {
      if (!this.pagination.total) return null;
      return Math.ceil(this.pagination.total / this.pagination.limit);
    },

    mustShowSearch() {
      return this.displaySearch || !!this.searchQuery;
    },

    studies() {
      return this.list.map(study => ({
        ...study,
        date: moment(study.date).format('DD MMM YYYY'),
      }));
    },
  },

  methods: {
    ...mapActions('study', {
      find: 'find',
    }),

    ...mapMutations('study', {
      clear: 'clearList',
    }),

    showEmailPrompt(study) {
      this.study = study;
      this.displayEmailPrompt = true;
    },

    async getStudies() {
      this.clear();

      if (this.searchQuery) {
        const query = {
          $search: {
            'patient.label': this.searchQuery,
            protocol: this.searchQuery,
          },
        };

        try {
          this.pagination = {
            ...this.pagination,
            ...await this.find({ query }),
          };
          delete this.pagination.data;
          this.table.isFiltered = true;
          return;
        } catch (e) {
          throw e;
        }
      }
      try {
        this.pagination = {
          ...this.pagination,
          ...await this.find(),
        };
        delete this.pagination.data;
        this.table.isFiltered = false;
        return;
      } catch (e) {
        throw e;
      }
    },

    openStudy(study) {
      const { _id } = study;
      this.$router.push({ name: 'EditStudy', params: { id: _id } });
    },

    noOrder(study) {
      return study.noOrder;
    },

    hasResults(study) {
      return !!study.results.length;
    },

    toggleSearch() {
      this.displaySearch = !this.displaySearch;

      if (this.displaySearch) {
        this.$nextTick(() => this.$refs.search.focus());
      } else if (this.table.isFiltered) {
        this.getStudies();
      }
    },

    clearSearch() {
      this.searchQuery = '';
      this.getStudies();
    },
  },

  mixins: [studyPrint, studySend],

  watch: {
    'pagination.current': function handlePageChange() {
      const query = {
        $skip: this.pagination.limit * (this.pagination.current - 1),
      };

      if (this.searchQuery) {
        query.$search = this.searchQuery;
      }

      this.clear();
      this.find({ query });
    },
  },
};
</script>

<style lang="less">
@import '~@/stylesheets/modules/colors';

.application--light .datatable {
  border-collapse: collapse;

  thead {
    tr {
      border-bottom: 1px solid #FFF;
      border-collapse: collapse;
      height: 3rem;
    }

    th {
      border-collapse: collapse;
      text-transform: uppercase;
      color: #999;

      &.column.sortable {
        outline: none;
      }

      &:last-child {
        width: 3rem;
        padding: 0;
      }
    }
  }

  tbody {
    tr {
      cursor: pointer;

      &:first-child td {
        border-top: none;
      }

      &:not(:last-child) {
        border-bottom: none;
      }
    }

    td:last-child {
      width: 3rem;
      padding: 0;
    }

    .noOrder {
      border-left: 5px solid @color-status-error;
      background: fade(@color-status-error, 7.5%);
    }

    .noResults {
      border-left: 5px solid @color-status-warning;
      background: fade(@color-status-warning, 7.5%);
    }

    .noOrder.noResults {
      border-left: 5px solid @color-status-error;
      box-shadow: inset 3px 0 0 @color-status-warning;
    }
  }

  tfoot {
    tr {
      border-top-color: #F2F2F2;
      background-color: #FAFAFA;
    }

    td {
      text-align: center;
    }
  }

}

.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 20rem;
}

.menu__content {
  min-width: 15rem !important;
  z-index: 1000 !important;
}

.list__tile .dialog__container, .list__tile .dialog__activator {
  align-items: center;
  display: flex !important;
  height: 100%;
  width: 100%;
}
</style>




// WEBPACK FOOTER //
// src/components/list.vue